import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const SECTION_ICONS = {
  'Ground-Based': '🔭',
  'Model': '🌐',
  'Radio Occultation': '📡',
  'Satellite': '🛰',
};

function AvailableDataViewer({ selectedDate, selectedCampaign }) {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!selectedDate || !selectedCampaign) return;

    const formattedDate = format(selectedDate, 'yyyy-MM-dd');
    setIsLoading(true);
    setError('');
    setData(null);

    fetch(`${API_BASE}/api/available-data/${selectedCampaign}/${formattedDate}`, {
      credentials: 'include'
    })
      .then(res => {
        if (res.status === 404) throw new Error('No availability data found for this date.');
        if (!res.ok) throw new Error('Failed to load availability data.');
        return res.json();
      })
      .then(json => setData(json))
      .catch(err => setError(err.message))
      .finally(() => setIsLoading(false));
  }, [selectedDate, selectedCampaign]);

  if (!selectedCampaign) {
    return (
      <div className="content-card">
        <p className="empty-state">Select a campaign to view available intercomparison data.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="content-card">
        <div className="loading-state">
          <span className="loading-spinner"></span>
          Loading availability data...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="content-card">
        <div className="empty-state">{error}</div>
      </div>
    );
  }

  if (!data) return null;

  const formattedDate = format(selectedDate, 'yyyy-MM-dd');

  return (
    <div className="content-card">
      <h2>Available Intercomparison Data</h2>
      <p className="avail-subtitle">
        {selectedCampaign} &mdash; {formattedDate}
      </p>

      {data.sections.length === 0 ? (
        <div className="empty-state">No data sources found in the availability report.</div>
      ) : (
        data.sections.map(section => {
          const icon = SECTION_ICONS[section.name] || '';
          return (
            <div key={section.name} className="avail-section">
              <div className="avail-section-header">
                {icon && <span>{icon} </span>}{section.name}
              </div>
              <table className="avail-table">
                <thead>
                  <tr>
                    <th>Source</th>
                    <th>Availability</th>
                  </tr>
                </thead>
                <tbody>
                  {section.sources.map(source => {
                    const isAvailable = source.status.toLowerCase().startsWith('available');
                    return (
                      <tr key={source.name}>
                        <td>
                          <div className="avail-source-name">{source.name}</div>
                          {source.path && (
                            <div className="avail-source-path">{source.path}</div>
                          )}
                        </td>
                        <td>
                          {isAvailable && source.files.length > 0 ? (
                            <details>
                              <summary>
                                <span className="avail-badge avail-badge--ok">{source.status}</span>
                              </summary>
                              <div className="avail-files">
                                {source.files.join(' · ')}
                              </div>
                            </details>
                          ) : (
                            <span className={`avail-badge ${isAvailable ? 'avail-badge--ok' : 'avail-badge--none'}`}>
                              {source.status || 'Not available'}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        })
      )}
    </div>
  );
}

export default AvailableDataViewer;
